import { escapeHtml } from '@theme/text-utils';
import { compareSearchspringSizeValues, CONSTANTS } from '@theme/searchspring-core';
import { registerImpressionTarget } from '@theme/searchspring-beacon';
import { promotedSwatchCodes } from '@theme/searchspring-filter-logic';
import '@theme/styleboard-button';

const COMBINED_LISTING_PRODUCT_TYPE = 'Combined-Listing';

/**
 * Decodes HTML entities in a string (e.g. `&lt;p&gt;` → `<p>`) so feed-encoded markup
 * can be injected as real HTML. Strings that already contain real tags are left intact.
 * @param {string} value
 * @returns {string}
 */
function decodeHtmlEntities(value) {
  if (!value || typeof value !== 'string') return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

/**
 * @typedef {{
 *   position: string;
 *   schemes: Record<string, string>;
 *   labels: Record<string, string>;
 *   style: { borderRadius: string; fontFamily: string; fontWeight: string; textTransform: string };
 * }} SearchspringBadgesConfig
 */

/** @type {SearchspringBadgesConfig | null | undefined} */
let cachedBadgesConfig;

/**
 * @returns {SearchspringBadgesConfig | null}
 */
function getSearchspringBadgesConfig() {
  if (cachedBadgesConfig !== undefined) return cachedBadgesConfig;
  const node = document.querySelector('script[data-searchspring-badges-config]');
  if (!node?.textContent) {
    cachedBadgesConfig = null;
    return null;
  }
  try {
    cachedBadgesConfig = JSON.parse(node.textContent);
  } catch (error) {
    console.warn('SearchspringProductCard: failed to parse badges config', error);
    cachedBadgesConfig = null;
  }
  return cachedBadgesConfig ?? null;
}

/**
 * Generates a Shopify CDN image URL with a specific width.
 * Inserts `_WIDTHx` before the file extension, preserving query strings.
 * @param {string} src
 * @param {number} width
 * @param {'webp' | null} [format]
 * @returns {string}
 */
function shopifyImageUrl(src, width, format = null) {
  if (!src) return src;
  let url = src.replace(/(\.[a-zA-Z]+)(\?|$)/, `_${width}x$1$2`);
  if (format) {
    url += (url.includes('?') ? '&' : '?') + `format=${format}`;
  }
  return url;
}

/**
 * @typedef {Object} SearchspringVariant
 * @property {string | number} [size]
 * @property {string} price
 * @property {string} [compareAtPrice]
 *
 * @typedef {Object} SearchspringColorOption
 * @property {string} key
 * @property {string} label
 * @property {string} [color]
 * @property {string[]} [images]
 *
 * @typedef {Object} SearchspringProduct
 * @property {string} [id]
 * @property {string} [title]
 * @property {string} [url]
 * @property {string} [handle]
 * @property {string} [product_type]
 * @property {string} [images]
 * @property {Record<string, SearchspringVariant[]>} [prices]
 * @property {Record<string, SearchspringVariant[]>} [mtoPrices]
 * @property {Record<string, SearchspringVariant[]>} [rtsPrices]
 * @property {Record<string, Record<string, SearchspringVariant[]>>} [pricesByType]
 * @property {string | string[]} [variant_price]
 * @property {{ key: string; value: string | number | boolean }[]} [parsedMetafields]
 * @property {{ colors?: SearchspringColorOption[] }} [searchspringOptions]
 * @property {string[]} [mfield_searchspring_main_product_labels]
 * @property {string} [mfield_searchspring_product_labels]
 * @property {string} [mfield_searchspring_fabric]
 * @property {string | Record<string, Record<string, string[]>>} [mfield_searchspring_product_urls]
 * @property {string[]} [mfield_searchspring_color_availability]
 * @property {string[]} [mfield_searchspring_color_groups]
 * @property {string | Record<string, Record<string, { message?: string; plp_message?: string; message_color?: string }>>} [mfield_product_data_sales_messages]
 *
 * @typedef {Record<string, Record<string, string[]>>} VariantLabelsMap
 *
 * @typedef {Object} SwatchOption
 * @property {string} value
 * @property {string} [title]
 * @property {string} [content]
 * @property {string} [style]
 * @property {string} [extraClass]
 *
 * @typedef {Object} SwatchGroupConfig
 * @property {'color' | 'size'} type
 * @property {number} limit
 * @property {(options: SwatchOption[], hiddenCount: number) => { text: string; title?: string }} [buildMoreText]
 */

class SearchspringProductCard extends HTMLElement {
  /** @type {SearchspringProduct} */
  product;

  /** @type {boolean} — true for the first 4 cards; enables eager loading */
  aboveTheFold = false;

  /** @type {number} */
  #colorOptionsToShow = 5;
  /** @type {number} */
  #sizeOptionsToShow = 5;
  /** @type {string | null} */
  #selectedColor = null;
  /** @type {string[]} */
  activeColorFilters = [];
  /** @type {string | null} */
  activeAvailabilityFilter = null;
  /** @type {string | null} */
  activeSizeFilter = null;
  /** @type {Map<string, string>} */
  colorCodeToLabelMap = new Map();
  /** @type {string[]} */
  promotedColors = [];
  /** @type {string} */
  promotedColorsMode = 'filter';
  /** @type {boolean} */
  isB2B = false;
  /** @type {string | null} */
  #selectedSize = null;
  /** @type {string | null} — the color the card opened on; styleboard adds fall back to it when no swatch is picked */
  #initialColor = null;
  /** @type {boolean} */
  #isPlaceholderImageShown = true;
  /** @type {string | null} — tracks the `data-columns` value the gallery was last rendered for */
  #galleryRenderedForColumns = null;
  /** @type {string[]} */
  #currentImages = [];
  /** @type {string[]} */
  #currentLabels = [];
  /** @type {VariantLabelsMap} */
  #availableVariantLabels = {};
  /** @type {Record<string, Record<string, { message?: string; plp_message?: string; message_color?: string }>> | undefined} — parsed sales messages cache */
  #salesMessages;
  /** @type {Record<string, Record<string, string[]>> | null} */
  #productUrls = null;
  /** @type {Record<string, string[]>} */
  #colorAvailability = {};
  /** @type {{ swatchClick: (event: MouseEvent) => void; mouseEnter: (event: MouseEvent) => void; pointerLeave: (event: PointerEvent) => void }} */
  #eventHandlers = {
    swatchClick: () => { },
    mouseEnter: () => { },
    pointerLeave: () => { },
  };
  /**
   * @type {{
   *   galleryContainer?: HTMLElement | null;
   *   labelsContainer?: HTMLElement | null;
   *   price?: HTMLElement | null;
   *   compareAtPrice?: HTMLElement | null;
   *   swatchContainer?: HTMLElement | null;
   *   swatches?: { color: HTMLButtonElement[]; size: HTMLButtonElement[] };
   * }}
   */
  #refs = {};

  constructor() {
    super();
    this.#eventHandlers = {
      swatchClick: (event) => this.handleSwatchClick(event),
      mouseEnter: (event) => this.mouseEnterHandler(event),
      pointerLeave: () => this.pointerLeaveHandler(),
    };
  }

  initState() {
    this.product = this.product || {};
    this.#salesMessages = undefined;
    this.setAvailableLabels();
    this.#productUrls = this.parseProductUrls();
    this.#colorAvailability = this.parseColorAvailability();

    // Precompute selections
    this.#selectedColor = this.precomputeSelectedColor();
    this.#initialColor = this.#selectedColor;
    this.#selectedSize = this.precomputeSelectedSize(this.#selectedColor);

    this.#isPlaceholderImageShown = true;

    // Set initial images based on preselected color
    const selectedOption = this.product?.searchspringOptions?.colors?.find(
      (option) => option.key === this.#selectedColor
    );
    const fallbackImages = this.getInitialImages();
    this.#currentImages =
      this.#selectedColor && selectedOption?.images?.length
        ? selectedOption.images
        : fallbackImages;

    // Set initial labels based on preselected color/size
    if (!this.#selectedColor || !this.#selectedSize) {
      this.#currentLabels = this.getInitialLabels();
    } else {
      const labelsObject = this.#availableVariantLabels;
      this.#currentLabels = labelsObject[this.#selectedColor]?.[this.#selectedSize] || [];
    }
  }

  precomputeSelectedColor() {
    if (!this.isCombinedProduct()) return null;

    const typePrices = this.#getTypePrices();
    const availableColorsOptions = Object.keys(typePrices);
    const searchspringColorOptions = this.product?.searchspringOptions?.colors || [];
    let filteredColorOptions = searchspringColorOptions.filter((option) =>
      availableColorsOptions.includes(option.key)
    ).sort((a, b) => availableColorsOptions.indexOf(a.key) - availableColorsOptions.indexOf(b.key));

    if (this.promotedColors && this.promotedColors.length > 0) {
      const promotedSet = new Set(this.promotedColors.map((c) => c.toUpperCase()));
      const extractCode = (key) => key.toUpperCase();

      if (this.promotedColorsMode === 'filter') {
        filteredColorOptions = filteredColorOptions.filter((option) =>
          promotedSet.has(extractCode(option.key))
        );
      } else if (this.promotedColorsMode === 'highlight') {
        const promoted = filteredColorOptions.filter((option) => promotedSet.has(extractCode(option.key)));
        const rest = filteredColorOptions.filter((option) => !promotedSet.has(extractCode(option.key)));
        filteredColorOptions = [...promoted, ...rest];
      }
    }

    if (filteredColorOptions.length === 0) {
      if (availableColorsOptions.length === 1) {
        return availableColorsOptions[0] ?? null;
      }
      return null;
    }

    let colorToSelect = this.findMatchingColorFromFilters(filteredColorOptions.map((option) => option.key));

    if (!colorToSelect && this.promotedColors && this.promotedColors.length > 0) {
      const promotedSet = new Set(this.promotedColors.map((c) => c.toUpperCase()));
      const matchingColor = filteredColorOptions.find((option) =>
        promotedSet.has(option.key.toUpperCase())
      );
      colorToSelect = matchingColor?.key || null;
    }

    if (!colorToSelect) {
      colorToSelect = filteredColorOptions[0]?.key || null;
    }

    return colorToSelect;
  }

  precomputeSelectedSize(selectedColor) {
    if (!this.isCombinedProduct() || !selectedColor) {
      return null;
    }

    const rtsUrls = this.#productUrls?.[CONSTANTS.PRODUCT_TYPES.RTS]?.[selectedColor];

    const prices = this.#getTypePrices();
    const variantsForColor = prices[selectedColor] || [];
    const availableSizes = variantsForColor
      .map((variant) => variant.size)
      .filter((size) => size !== null && typeof size !== 'undefined' && size !== '*')
      .map((size) => size?.toString() || '');
    const supportsAnySize = variantsForColor.some((variant) => variant.size === '*');

    function getAllSizes(obj = {}) {
      const sizes = Object.values(obj)
        .flat()
        .map((item) => item.size)
        .filter((size) => size !== null && typeof size !== 'undefined');

      return [...new Set(sizes)];
    }

    const availableSizeOptions = getAllSizes(prices);
    const sortedSizeOptions = availableSizeOptions.sort((a, b) =>
      compareSearchspringSizeValues(a, b)
    );

    for (const option of sortedSizeOptions) {
      const sizeValue = String(option);
      const isAvailable = supportsAnySize || availableSizes.includes(sizeValue);
      if (!isAvailable) continue;

      const hasRtsUrl = rtsUrls?.some((urlEntry) => {
        const colonIndex = urlEntry.indexOf(':');
        if (colonIndex === -1) return false;
        const entrySize = urlEntry.substring(0, colonIndex);
        return entrySize === sizeValue;
      }) || false;

      if (hasRtsUrl) {
        return sizeValue;
      }
    }

    for (const option of sortedSizeOptions) {
      const sizeValue = String(option);
      const isAvailable = supportsAnySize || availableSizes.includes(sizeValue);
      if (isAvailable) {
        return sizeValue;
      }
    }

    return null;
  }

  render() {
    if (!this.product || Object.keys(this.product).length === 0) {
      this.innerHTML = '';
      this.#refs = {};
      return;
    }
    this.renderProduct(this.product);
    this.cacheRefs();
  }

  cacheRefs() {
    this.#refs = {
      galleryContainer: /** @type {HTMLElement | null} */ (
        this.querySelector('.searchspring-product-card__gallery-container')
      ),
      labelsContainer: /** @type {HTMLElement | null} */ (
        this.querySelector('.searchspring-product-card__labels')
      ),
      price: /** @type {HTMLElement | null} */ (
        this.querySelector('.searchspring-product-card__price')
      ),
      compareAtPrice: /** @type {HTMLElement | null} */ (
        this.querySelector('.searchspring-product-card__price--compare-at')
      ),
      swatchContainer: /** @type {HTMLElement | null} */ (
        this.querySelector('.searchspring-product-card__swatches-container')
      ),
    };
    this.#refs.swatches = {
      color: Array.from(
        this.querySelectorAll('.searchspring-product-card__swatch[data-type="color"]'),
        (node) => /** @type {HTMLButtonElement} */(node)
      ),
      size: Array.from(
        this.querySelectorAll('.searchspring-product-card__swatch[data-type="size"]'),
        (node) => /** @type {HTMLButtonElement} */(node)
      ),
    };
  }

  bindEvents() {
    this.unbindEvents();
    this.#refs.swatchContainer?.addEventListener('click', this.#eventHandlers.swatchClick);
    this.addEventListener('mouseenter', this.#eventHandlers.mouseEnter);
    this.addEventListener('pointerleave', this.#eventHandlers.pointerLeave);
  }

  connectedCallback() {
    this.initState();
    this.render();
    this.bindEvents();
    // Registering on mount means appended pages and filtered re-renders are
    // covered without sweeping the grid after every render.
    registerImpressionTarget(this);
  }

  /**
   * The color the active color filters put first on this card, or null:
   * the most recent direct Color pick, else the first of the card's colors in
   * a picked group (see promotedSwatchCodes).
   * @param {string[]} cardCodes - the card's colors in swatch order
   * @returns {string | null}
   */
  findMatchingColorFromFilters(cardCodes) {
    if (!this.activeColorFilters || this.activeColorFilters.length === 0) {
      return null;
    }
    return promotedSwatchCodes(cardCodes, this.activeColorFilters)[0] ?? null;
  }

  setAvailableLabels() {
    this.#availableVariantLabels = this.getVariantLabels();
  }

  /**
   * Parses the mfield_searchspring_product_urls JSON field.
   * @returns {Record<string, Record<string, string[]>> | null}
   */
  parseProductUrls() {
    const urlsData = this.product?.mfield_searchspring_product_urls;
    if (!urlsData) return null;

    // If it's already an object (and not null), return as-is
    if (typeof urlsData === 'object' && urlsData !== null) {
      return /** @type {Record<string, Record<string, string[]>>} */ (urlsData);
    }

    // If it's a string, normalize and parse
    if (typeof urlsData === 'string') {
      try {
        const jsonString = urlsData.replace(/&quot;/g, '"');
        return JSON.parse(jsonString);
      } catch (error) {
        console.warn('SearchspringProductCard: failed to parse product URLs', error);
        return null;
      }
    }

    // Unsupported type
    return null;
  }

  /**
   * Parses the mfield_searchspring_color_availability field.
   * @returns {Record<string, string[]>}
   */
  parseColorAvailability() {
    /** @type {string[] | undefined} */
    const availabilityArray = this.product?.mfield_searchspring_color_availability;
    if (!availabilityArray || !Array.isArray(availabilityArray)) return /** @type {Record<string, string[]>} */({});

    /** @type {Record<string, string[]>} */
    const result = {};
    for (const item of availabilityArray) {
      const colonIndex = item.indexOf(':');
      if (colonIndex === -1) continue;
      const colorName = item.substring(0, colonIndex);
      const availabilityTypes = item
        .substring(colonIndex + 1)
        .split(',')
        .map((/** @type {string} */ t) => t.trim());
      result[colorName] = availabilityTypes;
    }
    return result;
  }

  /**
   * Returns the price data filtered by the active availability filter.
   * No filter or RTS → RTS prices; MTO → MTO prices.
   * Falls back to full prices if per-type data is not available.
   * @returns {Record<string, SearchspringVariant[]>}
   */
  #getTypePrices() {
    const prices = this.product?.prices || {};
    const rtsPrices = this.product?.rtsPrices;
    const mtoPrices = this.product?.mtoPrices;
    if (this.activeAvailabilityFilter === CONSTANTS.PRODUCT_TYPES.RTS) {
      return rtsPrices || prices;
    }
    if (this.activeAvailabilityFilter === CONSTANTS.PRODUCT_TYPES.MTO) {
      return mtoPrices || prices;
    }
    // No filter selected → prefer RTS
    if (rtsPrices && Object.keys(rtsPrices).length > 0) {
      if (mtoPrices && Object.keys(mtoPrices).length > 0) {
        // Merge RTS and MTO, with RTS taking precedence
        const merged = { ...rtsPrices };

        for (const color in mtoPrices) {
          if (mtoPrices[color] && !merged[color]) {
            merged[color] = mtoPrices[color];
          }
        }

        return merged;
      }

      return rtsPrices;
    }
    return mtoPrices || prices;
  }

  /**
   * Gets the preferred availability type for a color.
   * @param {string} color
   * @returns {string | null}
   */
  getPreferredAvailability(color) {
    const availabilityTypes = this.#colorAvailability[color];
    if (!availabilityTypes || availabilityTypes.length === 0) return null;

    if (this.isB2B) {
      return availabilityTypes.includes(CONSTANTS.PRODUCT_TYPES.RTS) ? CONSTANTS.PRODUCT_TYPES.RTS : null;
    }

    if (this.activeAvailabilityFilter && availabilityTypes.includes(this.activeAvailabilityFilter)) {
      return this.activeAvailabilityFilter;
    }

    if (availabilityTypes.includes(CONSTANTS.PRODUCT_TYPES.RTS)) {
      return CONSTANTS.PRODUCT_TYPES.RTS;
    }

    return availabilityTypes[0] || null;
  }

  /**
   * Gets the product URL for the selected color and size.
   * @param {string | null} color
   * @param {string | null} size
   * @returns {string | null}
   */
  getProductUrl(color, size) {
    if (!color || !this.#productUrls) return null;

    let availabilityType = this.getPreferredAvailability(color);
    if (!availabilityType) {
      if (this.isB2B) {
        const rtsUrls = this.#productUrls?.[CONSTANTS.PRODUCT_TYPES.RTS]?.[color];
        availabilityType = rtsUrls && rtsUrls.length > 0 ? CONSTANTS.PRODUCT_TYPES.RTS : null;
      } else {
        const preferredTypes = [CONSTANTS.PRODUCT_TYPES.RTS, CONSTANTS.PRODUCT_TYPES.MTO];
        const allTypes = [...preferredTypes, ...Object.keys(this.#productUrls).filter((t) => !preferredTypes.includes(t))];
        availabilityType = allTypes.find((type) => {
          const urls = this.#productUrls?.[type]?.[color];
          return urls && urls.length > 0;
        }) || null;
      }
    }

    if (!availabilityType) return null;

    const colorUrls = this.#productUrls[availabilityType]?.[color];
    if (!colorUrls || colorUrls.length === 0) return null;

    if (size) {
      const sizeUrl = colorUrls.find((urlEntry) => {
        const colonIndex = urlEntry.indexOf(':');
        if (colonIndex === -1) return false;
        const entrySize = urlEntry.substring(0, colonIndex);
        return entrySize === size;
      });
      if (sizeUrl) {
        const colonIndex = sizeUrl.indexOf(':');
        return sizeUrl.substring(colonIndex + 1);
      }
    }

    const firstUrl = colorUrls[0];
    if (firstUrl) {
      const colonIndex = firstUrl.indexOf(':');
      return firstUrl.substring(colonIndex + 1);
    }

    return null;
  }

  /**
   * Updates all product links to point to the selected variant.
   */
  updateProductLinks() {
    if (!this.isCombinedProduct()) return;

    const size = this.#selectedSize || this.activeSizeFilter;
    const newUrl = this.getProductUrl(this.#selectedColor, size);

    if (!newUrl) {
      this.resetProductLinks();
      return;
    }

    const links = this.querySelectorAll('a[href]');
    links.forEach((link) => {
      if (link instanceof HTMLAnchorElement) {
        link.href = newUrl;
      }
    });
    this.updateWishlistVariantId(newUrl);
  }

  /**
   * Resets product links to the original product URL.
   */
  resetProductLinks() {
    const originalUrl = this.product?.url || '';
    const links = this.querySelectorAll('a[href]');
    links.forEach((link) => {
      if (link instanceof HTMLAnchorElement) {
        link.href = originalUrl;
      }
    });
    this.updateWishlistVariantId(originalUrl);
  }

  /**
   * Syncs the wishlist button's data-variant-id with the given URL.
   * @param {string} url
   */
  updateWishlistVariantId(url) {
    const wishlistButton = this.querySelector('.product-card-wishlist-button');
    if (!wishlistButton) return;
    const { handle, variantId } = this.extractHandleAndVariantFromUrl(url);
    wishlistButton.setAttribute('data-variant-id', variantId);
    if (handle) {
      wishlistButton.setAttribute('data-product-handle', handle);
    }
  }

  /**
   * Gets the current product URL based on selected color/size or original URL.
   * @returns {string}
   */
  getCurrentProductUrl() {
    if (this.#selectedColor) {
      const size = this.#selectedSize || this.activeSizeFilter;
      const dynamicUrl = this.getProductUrl(this.#selectedColor, size);
      if (dynamicUrl) return dynamicUrl;
    }
    return this.product?.url || '';
  }

  /**
   * @param {'color' | 'size'} type
   * @returns {HTMLButtonElement[]}
   */
  getSwatchRefs(type) {
    return this.#refs.swatches?.[type] || [];
  }

  /**
   * @param {HTMLButtonElement} swatch
   * @param {boolean} isDisabled
   */
  setSwatchDisabled(swatch, isDisabled) {
    if (!swatch) return;
    swatch.classList.toggle('searchspring-product-card__swatch--disabled', Boolean(isDisabled));
    swatch.disabled = Boolean(isDisabled);
    swatch.setAttribute('aria-disabled', String(Boolean(isDisabled)));
  }

  /**
   * @param {HTMLButtonElement} swatch
   * @param {boolean} isPressed
   */
  setSwatchPressed(swatch, isPressed) {
    if (!swatch) return;
    swatch.classList.toggle('searchspring-product-card__swatch--active', Boolean(isPressed));
    swatch.setAttribute('aria-pressed', String(Boolean(isPressed)));
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  unbindEvents() {
    this.#refs.swatchContainer?.removeEventListener('click', this.#eventHandlers.swatchClick);
    this.removeEventListener('mouseenter', this.#eventHandlers.mouseEnter);
    this.removeEventListener('pointerleave', this.#eventHandlers.pointerLeave);
  }

  /**
   * @returns {string[]}
   */
  getInitialLabels() {
    return this.product?.mfield_searchspring_main_product_labels || [];
  }

  /**
   * @param {SearchspringProduct & { tags?: string[] | string; available?: boolean; ss_in_stock?: string | number | boolean }} product
   * @returns {string[]}
   */
  getBadgeKeys(product) {
    const rawTags = product?.tags;
    const tags = Array.isArray(rawTags)
      ? rawTags
      : typeof rawTags === 'string'
        ? rawTags.split(/[,|]/)
        : [];
    const tagSet = new Set(tags.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean));

    const records = this.getPriceRecords();

    const inStockField = product?.ss_in_stock;
    const isSoldOut =
      product?.available === false ||
      inStockField === false ||
      inStockField === '0' ||
      inStockField === 0;

    const keys = [];
    if (isSoldOut) {
      keys.push('sold_out');
    }
    if (tagSet.has('new')) keys.push('new');
    if (tagSet.has('bestseller')) keys.push('bestseller');
    if (tagSet.has('coming_soon')) keys.push('coming_soon');
    if (tagSet.has('jy_x_hl')) keys.push('jy_x_hl');
    return keys;
  }

  /**
   * @param {SearchspringProduct} product
   * @returns {string}
   */
  renderBadges(product) {
    const config = getSearchspringBadgesConfig();
    if (!config) return '';

    const keys = this.getBadgeKeys(product);
    if (!keys.length) return '';

    const style = config.style || {};
    const inlineStyle = [
      '--border-radius: 0px',
      '--padding-block-start: 0px',
      `--badge-border-radius: ${style.borderRadius}`,
      `--badge-font-family: ${style.fontFamily}`,
      `--badge-font-weight: ${style.fontWeight}`,
      `--badge-text-transform: ${style.textTransform}`,
    ].join('; ');

    const badgesHtml = keys
      .map((key) => {
        const scheme = config.schemes?.[key] || 'scheme-1';
        const label = config.labels?.[key] || '';
        const overrideTransform = key === 'jy_x_hl' ? ' style="--badge-text-transform: none;"' : '';
        return `
          <div class="product-badges__badge product-badges__badge--rectangle color-${escapeHtml(scheme)}"${overrideTransform}>
            ${escapeHtml(label)}
          </div>
        `;
      })
      .join('');

    return `
      <div class="product-badges product-badges--${escapeHtml(config.position || 'top-left')}" style="${escapeHtml(inlineStyle)}">
        ${badgesHtml}
      </div>
    `;
  }

  /**
   * @returns {string}
   */
  renderLabels() {
    return this.#currentLabels
      .map(
        (label) => `
            <div class="searchspring-product-card__label">${escapeHtml(label)}</div>
        `
      )
      .join('');
  }

  rerenderLabels() {
    const labelsContainer =
      this.#refs.labelsContainer || this.querySelector('.searchspring-product-card__labels');
    if (!labelsContainer) return;
    labelsContainer.innerHTML = this.renderLabels();
  }

  /**
   * @returns {VariantLabelsMap}
   */
  getVariantLabels() {
    const labelsString = this.product?.mfield_searchspring_product_labels || '';
    if (!labelsString) return {};

    const jsonString = labelsString.replace(/&quot;/g, '"');

    try {
      const parsed = JSON.parse(jsonString);
      const labels = Object.fromEntries(
        Object.entries(parsed).map(([color, obj]) => [
          color,
          Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [
              key,
              value.includes('|') ? value.split('|') : [value],
            ])
          ),
        ])
      );

      return labels;
    } catch (error) {
      console.warn('SearchspringProductCard: failed to parse variant labels', error);
      return {};
    }
  }

  /**
   * @returns {string[]}
   */
  getInitialImages() {
    if (!this.product?.images) return [];
    return this.product.images.split('|');
  }

  /**
   * @param {MouseEvent} [_event]
   */
  mouseEnterHandler(_event) {
    if (this.#currentImages.length === 0 || this.#currentImages.length === 1) return;
    if (!window.matchMedia('(hover: hover)').matches) return;

    const currentColumns =
      this.closest('.searchspring-product-grid-container')?.getAttribute('data-columns') ?? null;
    const columnsChanged = currentColumns !== this.#galleryRenderedForColumns;

    if (!this.#isPlaceholderImageShown && !columnsChanged) {
      this.#advanceToSecondSlide();
      return;
    }

    this.#isPlaceholderImageShown = false;
    this.updateGallery();
    this.#advanceToSecondSlide();
  }

  pointerLeaveHandler() {
    const slideshowComponent = this.querySelector('slideshow-component');
    if (
      slideshowComponent instanceof HTMLElement &&
      'select' in slideshowComponent &&
      typeof slideshowComponent.select === 'function'
    ) {
      slideshowComponent.select(0, undefined, { animate: false });
    }
  }

  #advanceToSecondSlide() {
    const slideshowComponent = this.querySelector('slideshow-component');
    if (
      slideshowComponent instanceof HTMLElement &&
      'select' in slideshowComponent &&
      typeof slideshowComponent.select === 'function'
    ) {
      slideshowComponent.select(1, undefined, { animate: false });
    }
  }

  /**
   * Re-renders picture elements with fresh `sizes` values after a grid column change.
   * Called externally when the column layout changes so the browser picks the right srcset entry.
   */
  refreshPictureSizes() {
    const galleryContainer =
      this.#refs.galleryContainer ||
      this.querySelector('.searchspring-product-card__gallery-container');
    if (!galleryContainer) return;

    if (!this.#isPlaceholderImageShown) {
      // Gallery (slideshow) is active — replace its HTML so the browser re-evaluates srcset
      this.updateGallery();
    } else {
      // Placeholder is still showing — replace only the placeholder picture
      galleryContainer.innerHTML = this.renderPlaceholderImage();
    }
  }

  updateGallery() {
    if (this.#currentImages.length === 0) return;
    const galleryContainer =
      this.#refs.galleryContainer ||
      this.querySelector('.searchspring-product-card__gallery-container');
    if (!galleryContainer) return;
    this.#galleryRenderedForColumns =
      this.closest('.searchspring-product-grid-container')?.getAttribute('data-columns') ?? null;
    galleryContainer.innerHTML = this.renderGallery();

    const slideshowComponent = galleryContainer.querySelector('slideshow-component');
    if (
      slideshowComponent instanceof HTMLElement &&
      'setupSlideshow' in slideshowComponent &&
      typeof slideshowComponent.setupSlideshow === 'function'
    ) {
      slideshowComponent.setupSlideshow();
    }
  }

  /**
   * @returns {string}
   */
  renderGallery() {
    const images = this.#currentImages;
    if (images.length === 0) return '';

    const gallerySlideTemplate = images
      .map(
        (imageUrl) => `
            <slideshow-slide ref="slides[]" style="view-timeline-name: --slide-1; --product-media-fit: cover;" class="product-media-container media-fit product-media-container--image">
               <div class="product-media" style="--ratio: 0.6666666666666666">
                    ${this.renderPicture(imageUrl, this.product.title || '')}
                </div>
            </slideshow-slide>
        `
      )
      .join('');

    return `
            <a href="${escapeHtml(this.getCurrentProductUrl())}" ref="cardGalleryLink" class="contents">
                <slideshow-component infinite>
                    <slideshow-container ref="slideshowContainer">
                        ${images.length > 1 ? `<slideshow-arrows>
                            <button class=" slideshow-control slideshow-control--previous slideshow-control--style-arrow button button-unstyled button-unstyled--transparent flip-x" aria-label="Previous slide" on:click="/previous" ref="previous">
                                <span class="svg-wrapper icon-arrow">
                                    <svg width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none">
                                        <path d="M7.5 16.5L13.0118 11.149C13.6627 10.517 13.6627 9.48295 13.0118 8.85101L7.5 3.5" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path>
                                    </svg>
                                </span>
                            </button>
                            <button class="slideshow-control slideshow-control--next slideshow-control--style-arrow button button-unstyled button-unstyled--transparent" aria-label="Next slide" on:click="/next" ref="next">
                                <span class="svg-wrapper icon-arrow">
                                <svg width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none">
                                    <path d="M7.5 16.5L13.0118 11.149C13.6627 10.517 13.6627 9.48295 13.0118 8.85101L7.5 3.5" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path>
                                </svg>
                                </span>
                            </button>
                        </slideshow-arrows>` : ''}

                        <slideshow-slides class="slideshow-slides-main-container" tabindex="-1" ref="scroller">
                            ${gallerySlideTemplate}
                        </slideshow-slides>
                    </slideshow-container>
                </slideshow-component>
            </a>
        `;
  }

  /**
   * Returns the CSS `sizes` hint based on the nearest grid container's current column layout.
   * Falls back to the default 4-col desktop / 2-col mobile layout when the container is not yet
   * in the DOM (e.g. during the initial `renderProduct` call).
   * @returns {string}
   */
  #getImageSizes() {
    const gridContainer = this.closest('.searchspring-product-grid-container')
      || document.querySelector('.searchspring-product-grid-container');
    const columns = gridContainer?.getAttribute('data-columns') || 'four-col';

    /** @type {Record<string, string>} */
    const desktopSizes = {
      'two-col': '50vw',
      'three-col': '33vw',
      'four-col': '25vw',
    };
    const desktopSize = desktopSizes[columns] || '25vw';

    // Mobile column layout is tracked separately; default is 2-col (50vw).
    // The mobile-single option yields 100vw.
    /** @type {Record<string, string>} */
    const mobileSizes = {
      'mobile-single': '100vw',
      'default': '50vw',
    };
    const mobileColumn = gridContainer?.getAttribute('data-columns-mobile') || 'default';
    const mobileSize = mobileSizes[mobileColumn] || '50vw';

    return `(max-width: 749px) ${mobileSize}, ${desktopSize}`;
  }

  /**
   * Renders a responsive `<picture>` element with WebP + JPEG srcsets.
   * Sizes cover the full range of column layouts (1-col mobile → 4-col desktop) at 1× and 2× DPR.
   * @param {string} src - Original Shopify CDN image URL
   * @param {string} alt
   * @returns {string}
   */
  renderPicture(src, alt) {
    if (!src) return '';
    const WIDTHS = [360, 540, 720, 900, 1080];
    const sizes = this.#getImageSizes();

    const webpSrcset = WIDTHS
      .map(w => `${escapeHtml(shopifyImageUrl(src, w, 'webp'))} ${w}w`)
      .join(', ');
    const jpegSrcset = WIDTHS
      .map(w => `${escapeHtml(shopifyImageUrl(src, w))} ${w}w`)
      .join(', ');
    const fallbackSrc = escapeHtml(shopifyImageUrl(src, 540));
    const escapedAlt = escapeHtml(alt);

    return `<picture class="w-full h-auto">
                <source type="image/webp" srcset="${webpSrcset}" sizes="${sizes}">
                <img srcset="${jpegSrcset}" sizes="${sizes}" src="${fallbackSrc}" alt="${escapedAlt}" loading="${this.aboveTheFold ? 'eager' : 'lazy'}"${this.aboveTheFold ? ' fetchpriority="high"' : ''}>
            </picture>`;
  }

  /**
   * @returns {string}
   */
  renderPlaceholderImage() {
    const placeholderSrc = this.#currentImages[0] || '';
    return this.#currentImages.length > 0
      ? `<a href="${escapeHtml(this.getCurrentProductUrl())}" class="contents" ref="cardGalleryLink"><div id="product-image-placeholder">${this.renderPicture(placeholderSrc, this.product?.title || '')}</div></a>`
      : this.renderImagePlaceholder();
  }

  /**
   * @param {SearchspringProduct} product
   */
  renderProduct(product) {
    const currentUrl = this.getCurrentProductUrl();

    const { price: priceText, compareAt: compareAtText } = this.getPriceDisplay();

    const template = `
            <div class="resource-list__item">
                <product-card class="product-card searchspring-product-card" data-product-id="${escapeHtml(product.id || '')}">
                    <a href="${escapeHtml(currentUrl)}" class="product-card__link" ref="productCardLink">${escapeHtml(product.title || '')}</a>
                        <div class="product-card__content product-grid__card searchspring-product-card_content">
                            ${this.renderActions(product)}
                            ${this.renderBadges(product)}
                            <div class="searchspring-product-card__labels">
                                ${this.renderLabels()}
                            </div>
                            <div class="relative overflow-hidden">
                                <div class="searchspring-product-card__gallery-container">
                                    ${this.renderPlaceholderImage()}
                                </div>
                                ${this.renderSwatches(product)}
                            </div>

                            <a href="${escapeHtml(currentUrl)}" title="${escapeHtml(product?.title || '')}" class="searchspring-product-card__title">${escapeHtml(product?.title || '')}</a>
                            ${this.renderFabric(product)}
                            <div class="searchspring-product-card__price_container">
                                <span class="searchspring-product-card__price">${priceText}</span>
                                <span style="${compareAtText ? 'display: inline;' : 'display: none;'}" class="searchspring-product-card__price searchspring-product-card__price--compare-at">${compareAtText || ''}</span>
                            </div>
                            ${this.renderSaleMessage()}
                        </div>
                </product-card>
            </div>
        `;

    this.innerHTML = template;
  }

  /**
   * Parses the `mfield_product_data_sales_messages` feed field (HTML-encoded JSON string or object).
   * Structure: { "ECOM": { "BCP": { message, plp_message, message_color } }, "MTO": {...} }
   * @returns {Record<string, Record<string, { message?: string; plp_message?: string; message_color?: string }>>}
   */
  getSalesMessages() {
    if (this.#salesMessages !== undefined) return this.#salesMessages;
    const raw = this.product?.mfield_product_data_sales_messages;
    if (!raw) {
      this.#salesMessages = {};
    } else if (typeof raw === 'object') {
      this.#salesMessages = raw;
    } else {
      try {
        this.#salesMessages = JSON.parse(raw.replace(/&quot;/g, '"').replace(/&#39;/g, "'"));
      } catch (error) {
        console.warn('SearchspringProductCard: failed to parse sales messages', error);
        this.#salesMessages = {};
      }
    }
    return this.#salesMessages;
  }

  /**
   * Resolves the sale message for a color code: ECOM preferred over MTO,
   * `plp_message` preferred over `message`.
   * @param {string | null} colorCode
   * @returns {{ html: string; color: string } | null}
   */
  getSaleMessageForColor(colorCode) {
    if (!colorCode) return null;
    const messages = this.getSalesMessages();
    for (const group of ['ECOM', 'MTO']) {
      const entry = messages?.[group]?.[colorCode];
      if (entry) {
        const html = entry.plp_message || entry.message;
        if (html) return { html: decodeHtmlEntities(html), color: entry.message_color || '' };
      }
    }
    return null;
  }

  /**
   * Renders the sale message container. Always present (hidden when the selected
   * color has no message) so `updateSaleMessage` only has to toggle its content.
   * @returns {string}
   */
  renderSaleMessage() {
    const message = this.getSaleMessageForColor(this.#selectedColor);
    if (!message) return '<div class="product-card__sale-message" hidden></div>';
    const styleAttr = message.color ? ` style="color: ${escapeHtml(message.color)}"` : '';
    return `<div class="product-card__sale-message"${styleAttr}>${message.html}</div>`;
  }

  /**
   * Re-syncs the sale message element with the currently selected color.
   * Hides it when the selected color has no message.
   */
  updateSaleMessage() {
    const messageEl = this.querySelector('.product-card__sale-message');
    if (!(messageEl instanceof HTMLElement)) return;

    const message = this.getSaleMessageForColor(this.#selectedColor);
    if (!message) {
      messageEl.innerHTML = '';
      messageEl.hidden = true;
      messageEl.removeAttribute('style');
      return;
    }

    messageEl.innerHTML = message.html;
    messageEl.hidden = false;
    // Assign via CSSOM rather than building the style attribute from a string: the
    // browser's CSS parser rejects invalid values, so a metafield color containing
    // quotes/semicolons can't break out and inject extra declarations (mirrors the
    // escaping applied on the initial `renderSaleMessage` path).
    if (message.color) {
      messageEl.style.color = message.color;
    } else {
      messageEl.removeAttribute('style');
    }
  }

  /**
   * @returns {boolean}
   */
  isCombinedProduct() {
    return Boolean(
      this.product?.product_type === COMBINED_LISTING_PRODUCT_TYPE &&
      Object.keys(this.product?.prices || {}).length > 0
    );
  }

  /**
   * @param {string | null} color
   * @param {string | null} size
   * @returns {SearchspringVariant[]}
   */
  getPriceRecords(color = null, size = null) {
    if (!this.isCombinedProduct()) {
      const rawPrice = this.product?.variant_price ?? '';
      const prices = Array.isArray(rawPrice) ? rawPrice : rawPrice.split('|');
      return prices
        .filter(Boolean)
        .map((/** @type {string} */ price) => ({ price }));
    }

    const pricesMatrix = this.#getTypePrices();
    if (!color) {
      return Object.values(pricesMatrix).flat();
    }

    const variants = pricesMatrix[color] || [];
    if (!size) {
      return variants;
    }

    const matched = variants.find(
      (variant) => variant.size?.toString() === size.toString() || variant.size === '*'
    );
    return matched ? [matched] : [];
  }

  /**
   * Product types whose prices the card shows: the active availability filter's
   * type, RTS only for B2B (no MTO), otherwise both. Falls back to whatever types
   * the product has when none of those are (e.g. B2B on an MTO-only recommendation).
   * @param {Record<string, unknown>} pricesByType
   * @returns {string[]}
   */
  #getDisplayPriceTypes(pricesByType) {
    const { RTS, MTO } = CONSTANTS.PRODUCT_TYPES;
    let types = [RTS, MTO];
    if (this.isB2B || this.activeAvailabilityFilter === RTS) types = [RTS];
    else if (this.activeAvailabilityFilter === MTO) types = [MTO];
    const available = types.filter((type) => pricesByType[type]);
    return available.length > 0 ? available : Object.keys(pricesByType);
  }

  /**
   * Price records for the card's price: the selected color (or every color when none
   * is selected) across the displayed product types, narrowed to the selected size.
   * @returns {SearchspringVariant[]}
   */
  getDisplayPriceRecords() {
    if (!this.isCombinedProduct()) return this.getPriceRecords();

    const pricesByType = this.product?.pricesByType || {};
    const matrices = this.#getDisplayPriceTypes(pricesByType).map((type) => pricesByType[type] || {});
    const color = this.#selectedColor;
    const records = matrices.flatMap((matrix) => (color ? matrix[color] || [] : Object.values(matrix).flat()));
    if (!this.#selectedSize) return records;

    const size = this.#selectedSize.toString();
    const sizeRecords = records.filter((record) => record.size?.toString() === size || record.size === '*');
    return sizeRecords.length > 0 ? sizeRecords : records;
  }

  /**
   * @returns {{ price: string; compareAt: string | null }}
   */
  getPriceDisplay() {
    const records = this.getDisplayPriceRecords();
    return {
      price: this.formatPriceSummary(records) || '--',
      compareAt: this.getCompareAtSummary(records),
    };
  }

  /**
   * A single price when all records agree, otherwise the "$min – $max" range.
   * @param {SearchspringVariant[]} records
   * @returns {string}
   */
  formatPriceSummary(records) {
    if (!records || records.length === 0) return '';
    const numericPrices = records
      .map((record) => parseFloat(record.price))
      .filter((price) => Number.isFinite(price));
    if (numericPrices.length === 0) return '';
    const lowest = Math.min(...numericPrices);
    const highest = Math.max(...numericPrices);
    const lowestText = this.formatPrice(lowest.toFixed(2));
    if (lowest === highest) return lowestText;
    return `${lowestText} – ${this.formatPrice(highest.toFixed(2))}`;
  }

  /**
   * @param {{ price: string; compareAt?: string | null }} param0
   */
  setPriceText({ price, compareAt = null }) {
    if (this.#refs.compareAtPrice) {
      if (compareAt) {
        this.#refs.compareAtPrice.textContent = compareAt;
        this.#refs.compareAtPrice.style.display = 'inline';
      } else {
        this.#refs.compareAtPrice.style.display = 'none';
      }
    }
    if (!this.#refs.price || !price) return;
    this.#refs.price.textContent = price;
  }

  /**
   * The struck-through compare-at price, matching snippets/color-price-range.liquid:
   * only when every price matches and every one is marked down (a range is shown
   * without sale styling). Shows the lowest compare-at.
   * @param {SearchspringVariant[]} records
   * @returns {string | null}
   */
  getCompareAtSummary(records) {
    if (!records || records.length === 0) return null;
    const prices = records.map((r) => parseFloat(r.price));
    if (!prices.every((price) => Number.isFinite(price) && price === prices[0])) return null;
    const compareAts = records.map((r) => parseFloat(r.compareAtPrice ?? ''));
    if (!compareAts.every((compareAt) => Number.isFinite(compareAt) && compareAt > prices[0])) return null;
    return this.formatPrice(Math.min(...compareAts).toFixed(2));
  }

  /**
   * @param {string} price
   * @returns {string}
   */
  formatPrice(price) {
    if (!price) return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(price));
  }

  /**
   * Extracts the product handle and variant ID from a product URL
   * of the form `/products/{handle}?variant={variant_id}`.
   * @param {string | null | undefined} url
   * @returns {{ handle: string, variantId: string }}
   */
  extractHandleAndVariantFromUrl(url) {
    if (!url) return { handle: '', variantId: '' };
    const queryIndex = url.indexOf('?');
    const pathPart = queryIndex === -1 ? url : url.substring(0, queryIndex);
    const match = pathPart.match(/\/products\/([^/?#]+)/);
    const handle = match?.[1] || '';
    const variantId = queryIndex === -1
      ? ''
      : new URLSearchParams(url.substring(queryIndex + 1)).get('variant') || '';
    return { handle, variantId };
  }

  /**
   * @param {SearchspringProduct} product
   * @returns {string}
   */
  renderWishlistButton(product) {
    const { handle, variantId } = this.extractHandleAndVariantFromUrl(product?.url);
    const productHandle = handle || product.handle || '';
    return `
            <button class="product-card-wishlist-button button button--tertiary" type="button" data-product-handle="${escapeHtml(productHandle)}" data-variant-id="${escapeHtml(variantId)}" aria-label="Add to favorites" aria-pressed="false">
                <svg class="product-card-wishlist-button-icon--default" width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none">
                    <path d="M10.996 16.9292C10.724 17.0236 10.276 17.0236 10.004 16.9292C7.684 16.1506 2.5 12.9022 2.5 7.39663C2.5 4.96629 4.492 3 6.948 3C8.404 3 9.692 3.69214 10.5 4.7618C11.308 3.69214 12.604 3 14.052 3C16.508 3 18.5 4.96629 18.5 7.39663C18.5 12.9022 13.316 16.1506 10.996 16.9292Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
                <svg class="product-card-wishlist-button-icon--selected" width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none">
                    <path d="M10.996 16.9292C10.724 17.0236 10.276 17.0236 10.004 16.9292C7.684 16.1506 2.5 12.9022 2.5 7.39663C2.5 4.96629 4.492 3 6.948 3C8.404 3 9.692 3.69214 10.5 4.7618C11.308 3.69214 12.604 3 14.052 3C16.508 3 18.5 4.96629 18.5 7.39663C18.5 12.9022 13.316 16.1506 10.996 16.9292Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
            </button>
        `;
  }

  /**
   * The wishlist button, preceded by the Add to Style Board button.
   * Needs the feed's `sku`, which is the product's style number, and a color: the
   * styleboards API rejects items without one, and cards with no price data
   * (sold-out products) have no colors.
   * @param {SearchspringProduct & { sku?: string }} product
   * @returns {string}
   */
  renderActions(product) {
    if (!product.sku || !this.#initialColor) {
      return this.renderWishlistButton(product);
    }
    return `
            <div class="product-card__actions">
                ${this.renderStyleboardButton()}
                ${this.renderWishlistButton(product)}
            </div>
        `;
  }

  /**
   * @returns {string}
   */
  renderStyleboardButton() {
    const { style, color, type, productHandle, variantId } = this.getStyleboardProduct();
    return `
            <styleboard-button class="styleboard-button" data-style="${escapeHtml(style)}" data-color="${escapeHtml(color || '')}" data-type="${type}" data-product-handle="${escapeHtml(productHandle)}" data-variant-id="${escapeHtml(variantId || '')}">
                <button class="styleboard-button__button button button--tertiary" type="button" aria-label="Add to style board">
                    <svg class="styleboard-button__icon--default" width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none">
                        <path d="M7.82432 5.5C7.82432 4.11929 8.91336 3 10.2568 3C11.6002 3 12.6892 4.11929 12.6892 5.5V5.95564C12.6892 6.92315 12.2285 7.82942 11.4557 8.38193L1.91443 15.2037C1.65434 15.3897 1.5 15.6897 1.5 16.0095C1.5 16.5565 1.94348 17 2.49054 17H18.5095C19.0565 17 19.5 16.5565 19.5 16.0095C19.5 15.6897 19.3457 15.3897 19.0856 15.2037L18.5935 14.8518M12.6892 10.7817H15.6892M15.6892 10.7817H18.6892M15.6892 10.7817V13.7817M15.6892 10.7817V7.7817" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                    <svg class="styleboard-button__icon--selected" width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 16 16" fill="none">
                        <path fill="currentColor" d="M0 12.7282C0.000111845 12.318 0.204296 11.9333 0.547859 11.6947L8.27722 6.32626C8.76727 5.98582 9.06439 5.42321 9.06439 4.81726V4.45898C9.06439 3.63151 8.39629 2.98384 7.6008 2.98359C6.80511 2.98359 6.13622 3.63135 6.13622 4.45898C6.13622 4.73055 5.90948 4.95071 5.62989 4.95077C5.35026 4.95077 5.12357 4.73059 5.12357 4.45898C5.12357 3.11371 6.21987 2 7.6008 2C8.98152 2.00025 10.077 3.11386 10.077 4.45898V4.81726C10.077 5.73383 9.6275 6.59729 8.86562 7.12639L1.13626 12.4948C1.05878 12.5488 1.01276 12.6356 1.01265 12.7282C1.01265 12.887 1.14499 13.0162 1.30833 13.0164H14.2859C14.4494 13.0164 14.5825 12.8871 14.5825 12.7282C14.5824 12.6355 14.5356 12.5488 14.4579 12.4948L14.0594 12.2182C13.8319 12.0602 13.7791 11.7524 13.9417 11.5314C14.1044 11.3105 14.4213 11.2601 14.6488 11.4181L15.0473 11.6947C15.3908 11.9333 15.5951 12.3181 15.5952 12.7282C15.5952 13.4303 15.0087 14 14.2859 14H1.30833C0.58572 13.9998 0 13.4302 0 12.7282ZM15.1294 6.08517C15.3235 5.88967 15.6441 5.8832 15.8454 6.07172C16.0466 6.26016 16.052 6.57162 15.8582 6.76715L12.3328 10.3173L12.3318 10.3192C11.621 11.0301 10.4545 11.0302 9.74378 10.3192C9.73895 10.3144 9.73456 10.3089 9.72993 10.3038L8.63521 9.1147C8.44897 8.91228 8.46656 8.60126 8.67476 8.42024C8.88313 8.23937 9.20336 8.25652 9.38975 8.45866L10.4706 9.63339L10.531 9.68911C10.846 9.94608 11.3114 9.92704 11.6049 9.63339L15.1294 6.08517Z"></path>
                    </svg>
                </button>
            </styleboard-button>
        `;
  }

  /**
   * The style board item for what the card shows: its style, the picked color
   * (or the color it opened on), and the variant its links point at.
   * @returns {{ style: string, color: string | null, type: string, productHandle: string, variantId: string | null }}
   */
  getStyleboardProduct() {
    const product = /** @type {SearchspringProduct & { sku?: string }} */ (this.product);
    const color = this.#selectedColor || this.#initialColor;
    const url = this.getProductUrl(color, this.#selectedSize || this.activeSizeFilter) || product.url;
    const { handle, variantId } = this.extractHandleAndVariantFromUrl(url);
    const availability = color ? this.getPreferredAvailability(color) : null;
    return {
      style: product.sku || '',
      color,
      type: availability === CONSTANTS.PRODUCT_TYPES.MTO ? 'mto' : 'ecom',
      productHandle: handle || product.handle || '',
      variantId: variantId || null,
    };
  }

  /**
   * Hands the swatch selection to the Add to Style Board button; a color change makes
   * it re-check whether that color is already in a board.
   */
  syncStyleboardButton() {
    const button = this.querySelector('styleboard-button');
    if (!(button instanceof HTMLElement)) return;
    const { color, type, productHandle, variantId } = this.getStyleboardProduct();
    Object.assign(button.dataset, { color: color || '', type, productHandle, variantId: variantId || '' });
  }

  /**
   * @param {MouseEvent} event
   */
  handleSwatchClick(event) {
    const eventTarget = event.target instanceof Element ? event.target : null;
    const target = /** @type {HTMLButtonElement | null} */ (
      eventTarget?.closest('.searchspring-product-card__swatch')
    );
    if (!target || target.disabled) return;
    const type = target.getAttribute('data-type');
    const value = target.getAttribute('data-value');
    if (!type || value === 'more') return;
    if (type !== 'color' && type !== 'size') return;

    if (type === 'color') {
      this.colorSwatchClickHandler(target);
    } else if (type === 'size') {
      this.sizeSwatchClickHandler(target);
    }
    this.updateAvailableOptions(type);
    if (type === 'color') {
      this.#syncSelectedSizeWithColor();
    }
    this.updatePriceDisplay();
    this.updateCurrentLabels();
    this.updateProductLinks();
    this.syncStyleboardButton();
    if (type === 'color') {
      this.updateSaleMessage();
    }
  }

  /**
   * @param {'color' | 'size'} type
   */
  updateAvailableOptions(type) {
    const prices = this.#getTypePrices();
    if (!prices || Object.keys(prices).length === 0) return;

    if (type === 'color') {
      const sizeSwatches = this.getSwatchRefs('size');
      if (!sizeSwatches.length) return;
      const selectedColor = this.#selectedColor;
      if (!selectedColor || !prices[selectedColor]) {
        sizeSwatches.forEach((swatch) => {
          this.setSwatchDisabled(swatch, false);
          swatch.style.display = '';
        });
        this.#updateMoreButton('size', sizeSwatches);
        return;
      }

      const variantsForColor = prices[selectedColor] || [];
      const availableSizes = /** @type {string[]} */ (
        variantsForColor
          .map((variant) => variant.size)
          .filter((size) => size !== null && typeof size !== 'undefined' && size !== '*')
          .map((size) => size?.toString() || '')
      );
      const supportsAnySize = variantsForColor.some((variant) => variant.size === '*');
      sizeSwatches.forEach((swatch) => {
        const swatchValue = swatch.getAttribute('data-value') || '';
        const isAvailable = supportsAnySize || availableSizes.includes(swatchValue);
        this.setSwatchDisabled(swatch, !isAvailable);
        swatch.style.display = isAvailable ? '' : 'none';
      });
      this.#updateMoreButton('size', sizeSwatches);
    } else if (type === 'size') {
      // Colors must stay visible regardless of the selected size — picking a
      // color that lacks the size falls back to its first available size.
      const colorSwatches = this.getSwatchRefs('color');
      colorSwatches.forEach((swatch) => this.setSwatchDisabled(swatch, false));
    }
  }

  /**
   * If the selected size is not offered for the newly selected color,
   * fall back to the first size available for that color.
   * Relies on `updateAvailableOptions('color')` having already synced
   * the disabled state of the size swatches.
   */
  #syncSelectedSizeWithColor() {
    if (!this.#selectedSize) return;
    const sizeSwatches = this.getSwatchRefs('size');
    if (!sizeSwatches.length) return;

    const current = sizeSwatches.find(
      (swatch) => (swatch.getAttribute('data-value') || '') === this.#selectedSize
    );
    if (current && !current.disabled) return;

    const firstAvailable = sizeSwatches.find((swatch) => !swatch.disabled) || null;
    this.#selectedSize = firstAvailable?.getAttribute('data-value') || null;
    sizeSwatches.forEach((swatch) =>
      this.setSwatchPressed(swatch, Boolean(firstAvailable) && swatch === firstAvailable)
    );
  }

  /**
   * Update or hide the "more" button for a swatch group based on visible swatches.
   * @param {'color' | 'size'} type
   * @param {HTMLButtonElement[]} swatches
   */
  #updateMoreButton(type, swatches) {
    const container = type === 'size'
      ? this.querySelector('.searchspring-product-card__swatches--size')
      : this.querySelector('.searchspring-product-card__swatches[data-kind="color"]');
    if (!container) return;

    const moreButton = /** @type {HTMLButtonElement | null} */ (
      container.querySelector('.searchspring-product-card__more-swatches')
    );

    const visibleSwatches = swatches.filter((s) => !s.classList.contains('searchspring-product-card__swatch--disabled'));
    const limit = type === 'size' ? this.#sizeOptionsToShow : this.#colorOptionsToShow;
    const hiddenCount = Math.max(visibleSwatches.length - limit, 0);

    // Show all visible swatches up to the limit, hide the rest
    let visibleIndex = 0;
    for (const swatch of swatches) {
      if (swatch.classList.contains('searchspring-product-card__swatch--disabled')) continue;
      if (visibleIndex < limit) {
        swatch.style.display = '';
      } else {
        swatch.style.display = 'none';
      }
      visibleIndex++;
    }

    if (!moreButton) {
      // If there are hidden items but no button yet, create one
      if (hiddenCount > 0 && type === 'size') {
        const lastVisible = visibleSwatches[visibleSwatches.length - 1];
        const lastValue = lastVisible?.getAttribute('data-value') || '';
        const text = `up to ${escapeHtml(lastValue)}`;
        const btn = document.createElement('button');
        btn.className = 'searchspring-product-card__more-swatches';
        btn.type = 'button';
        btn.dataset.type = type;
        btn.dataset.value = 'more';
        btn.title = 'More sizes';
        btn.setAttribute('aria-label', 'More sizes');
        btn.textContent = text;
        container.appendChild(btn);
      }
      return;
    }

    if (hiddenCount <= 0) {
      moreButton.style.display = 'none';
      return;
    }

    moreButton.style.display = '';
    if (type === 'size') {
      const lastVisible = visibleSwatches[visibleSwatches.length - 1];
      const lastValue = lastVisible?.getAttribute('data-value') || '';
      moreButton.textContent = `up to ${lastValue}`;
      moreButton.title = 'More sizes';
      moreButton.setAttribute('aria-label', 'More sizes');
    } else {
      moreButton.textContent = `+ ${hiddenCount} more`;
      moreButton.title = 'More colors';
      moreButton.setAttribute('aria-label', 'More colors');
    }
  }

  /**
   * @param {HTMLButtonElement} target
   */
  colorSwatchClickHandler(target) {
    const colorSwatches = this.getSwatchRefs('color');
    const swatchValue = target.getAttribute('data-value') || '';
    const isActive = this.#selectedColor === swatchValue;

    colorSwatches.forEach((swatch) => this.setSwatchPressed(swatch, false));
    if (!isActive) {
      this.setSwatchPressed(target, true);
    }

    this.#selectedColor = isActive ? null : swatchValue;
    const selectedOption = this.product?.searchspringOptions?.colors?.find(
      (option) => option.key === this.#selectedColor
    );

    const fallbackImages = this.getInitialImages();
    this.#currentImages =
      this.#selectedColor && selectedOption?.images?.length
        ? selectedOption.images
        : fallbackImages;

    const isHoverDevice = window.matchMedia('(hover: hover)').matches;
    if (this.#isPlaceholderImageShown || !isHoverDevice) {
      // Keep the single-image placeholder before hover and on touch devices —
      // rendering the multi-slide slideshow causes iOS scroll-snap to occasionally
      // land on the second slide.
      const galleryContainer =
        this.#refs.galleryContainer ||
        this.querySelector('.searchspring-product-card__gallery-container');
      if (galleryContainer) {
        galleryContainer.innerHTML = this.renderPlaceholderImage();
      }
      return;
    }

    this.updateGallery();
  }

  /**
   * @param {HTMLButtonElement} target
   */
  sizeSwatchClickHandler(target) {
    const sizeSwatches = this.getSwatchRefs('size');
    const swatchValue = target.getAttribute('data-value') || '';
    const isActive = this.#selectedSize === swatchValue;

    sizeSwatches.forEach((swatch) => this.setSwatchPressed(swatch, false));
    if (!isActive) {
      this.setSwatchPressed(target, true);
    }

    this.#selectedSize = isActive ? null : swatchValue;
  }

  updatePriceDisplay() {
    this.setPriceText(this.getPriceDisplay());
  }

  updateCurrentLabels() {
    if (!this.#selectedColor || !this.#selectedSize) {
      this.#currentLabels = this.getInitialLabels();
    } else {
      const labelsObject = this.#availableVariantLabels;
      const updatedLabels = labelsObject[this.#selectedColor]?.[this.#selectedSize] || [];
      this.#currentLabels = updatedLabels;
    }
    this.rerenderLabels();
  }

  /**
   * @param {SearchspringProduct} product
   * @returns {string}
   */
  renderSwatches(product) {
    if (
      !product ||
      product?.product_type !== COMBINED_LISTING_PRODUCT_TYPE ||
      Object.keys(product.prices || {}).length === 0
    )
      return '';
    const colorSwatches = this.renderColorSwatches(product);
    const sizeSwatches = this.renderSizeSwatches(product);
    if (!colorSwatches && !sizeSwatches) return '';
    const swatchesTemplate = `
            <div class="searchspring-product-card__swatches-container">
                ${colorSwatches
        ? `
                    <div class="searchspring-product-card__swatches" data-kind="color" role="group" aria-label="Color options">
                        ${colorSwatches}
                    </div>
                `
        : ''
      }
                ${sizeSwatches
        ? `
                    <div class="searchspring-product-card__swatches searchspring-product-card__swatches--size" data-kind="size" role="group" aria-label="Size options">
                        ${sizeSwatches}
                    </div>
                `
        : ''
      }
            </div>
        `;
    return swatchesTemplate;
  }

  /**
   * @param {SearchspringProduct} product
   * @returns {string}
   */
  renderColorSwatches(product) {
    const searchspringColorOptions = /** @type {SearchspringColorOption[] | undefined} */ (
      product?.searchspringOptions?.colors
    );
    const typePrices = this.#getTypePrices();
    const availableColorsOptions = Object.keys(typePrices);
    let filteredColorOptions = searchspringColorOptions?.filter((option) =>
      availableColorsOptions.includes(option.key)
    ).sort((a, b) =>
      availableColorsOptions.indexOf(a.key) - availableColorsOptions.indexOf(b.key)
    );

    if (!filteredColorOptions || filteredColorOptions.length === 0) return '';

    // Apply promoted colors filtering/reordering
    if (this.promotedColors && this.promotedColors.length > 0) {
      const promotedSet = new Set(this.promotedColors.map((c) => c.toUpperCase()));

      /**
       * Extract color code from a color key.
       * The key format in mfield_searchspring_colors is typically the color name (e.g. "Agave")
       * or a color code (e.g. "AGV"). We check the uppercase key against promoted codes.
       * @param {string} key
       * @returns {string}
       */
      const extractCode = (key) => key.toUpperCase();

      if (this.promotedColorsMode === 'filter') {
        filteredColorOptions = filteredColorOptions.filter((option) =>
          promotedSet.has(extractCode(option.key))
        );
      } else if (this.promotedColorsMode === 'highlight') {
        // Reorder: promoted colors first, then the rest
        const promoted = filteredColorOptions.filter((option) => promotedSet.has(extractCode(option.key)));
        const rest = filteredColorOptions.filter((option) => !promotedSet.has(extractCode(option.key)));
        filteredColorOptions = [...promoted, ...rest];
      }
    }

    if (filteredColorOptions.length === 0) return '';

    // Put the colors the filters picked first, so they aren't hidden behind
    // "+ N more": direct Color picks, then the colors of a picked group (or
    // the search query) in the card's own order, i.e. Ready to Ship first.
    if (this.activeColorFilters && this.activeColorFilters.length > 0) {
      const promoted = promotedSwatchCodes(
        filteredColorOptions.map((option) => option.key),
        this.activeColorFilters
      );
      const rank = new Map(promoted.map((code, index) => [code, index]));
      filteredColorOptions = [
        ...filteredColorOptions
          .filter((option) => rank.has(option.key))
          .sort((a, b) => (rank.get(a.key) ?? 0) - (rank.get(b.key) ?? 0)),
        ...filteredColorOptions.filter((option) => !rank.has(option.key)),
      ];
    }

    const options = filteredColorOptions.map((option) => ({
      value: option.key,
      title: this.colorCodeToLabelMap.get(option.key) || option.label || option.key,
      content: '',
      style: option.color ? `background-color: ${escapeHtml(option.color)}` : 'background-color: #cccccc',
      extraClass: 'searchspring-product-card__swatch--color',
    }));

    return this.renderSwatchGroup(options, {
      type: 'color',
      limit: this.#colorOptionsToShow,
      buildMoreText: (_options, hiddenCount) => ({
        text: `+ ${hiddenCount} more`,
        title: 'More colors',
      }),
    });
  }

  /**
   * @param {SearchspringProduct} product
   * @returns {string}
   */
  renderSizeSwatches(product) {
    /**
     * @param {Record<string, SearchspringVariant[]>} obj
     * @returns {Array<string | number>}
     */
    function getAllSizes(obj = {}) {
      const sizes = Object.values(obj)
        .flat()
        .map((item) => item.size)
        .filter((size) => size !== null && typeof size !== 'undefined');

      return [...new Set(sizes)];
    }

    const availableSizeOptions = getAllSizes(this.#getTypePrices());

    if (!availableSizeOptions || availableSizeOptions.length === 0) return '';

    const sortedSizeOptions = availableSizeOptions.sort((a, b) =>
      compareSearchspringSizeValues(a, b)
    );

    const prices = this.#getTypePrices();
    const selectedColor = this.#selectedColor;
    const variantsForColor = selectedColor ? (prices[selectedColor] || []) : [];
    const availableSizes = variantsForColor
      .map((variant) => variant.size)
      .filter((size) => size !== null && typeof size !== 'undefined' && size !== '*')
      .map((size) => size?.toString() || '');
    const supportsAnySize = variantsForColor.some((variant) => variant.size === '*');

    const options = sortedSizeOptions.map((option) => {
      const value = String(option);
      const isAvailable = selectedColor ? (supportsAnySize || availableSizes.includes(value)) : true;
      return {
        value,
        title: option === '*' ? 'All sizes' : String(option),
        content: String(option),
        extraClass: 'searchspring-product-card__swatch--size',
        isAvailable,
      };
    });

    const availableOptions = options.filter((opt) => opt.isAvailable);
    const limit = this.#sizeOptionsToShow;
    const hiddenCount = Math.max(availableOptions.length - limit, 0);

    let visibleIndex = 0;
    const swatchButtons = options.map((option) => {
      const isActive = option.value === this.#selectedSize;
      const isDisabled = !option.isAvailable;
      let inlineStyle = '';

      if (isDisabled) {
        inlineStyle = 'display: none';
      } else {
        if (visibleIndex >= limit) {
          inlineStyle = 'display: none';
        }
        visibleIndex++;
      }

      return this.renderSwatchButton('size', option, isActive, isDisabled, inlineStyle);
    }).join('');

    if (hiddenCount > 0) {
      const lastVisible = availableOptions[availableOptions.length - 1];
      const lastValue = lastVisible?.value || '';
      const text = `up to ${lastValue}`;
      const title = 'More sizes';
      return `${swatchButtons} ${this.renderMoreSwatches({ type: 'size', text, title })}`;
    }

    return swatchButtons;
  }

  /**
   * @param {SwatchOption[]} options
   * @param {SwatchGroupConfig} param1
   * @returns {string}
   */
  renderSwatchGroup(options, { type, limit, buildMoreText }) {
    if (!options?.length) return '';
    const visibleOptions = options.slice(0, limit);
    const hiddenCount = Math.max(options.length - limit, 0);
    const swatchButtons = visibleOptions
      .map((option) => {
        const isActive = (type === 'color' && option.value === this.#selectedColor) ||
          (type === 'size' && option.value === this.#selectedSize);
        return this.renderSwatchButton(type, option, isActive);
      })
      .join('');

    if (!hiddenCount) {
      return swatchButtons;
    }

    /** @type {{ text: string; title?: string }} */
    const textConfig = buildMoreText
      ? buildMoreText(options, hiddenCount)
      : { text: `+ ${hiddenCount}`, title: 'More options' };
    const { text, title } = textConfig;
    return `${swatchButtons} ${this.renderMoreSwatches({ type, text, title })}`;
  }

  /**
   * @param {'color' | 'size'} type
   * @param {SwatchOption} option
   * @param {boolean} isActive
   * @param {boolean} isDisabled
   * @param {string} inlineStyle
   * @returns {string}
   */
  renderSwatchButton(type, { value, title = 'Option', content = '', style = '', extraClass = '' }, isActive = false, isDisabled = false, inlineStyle = '') {
    const classes = ['searchspring-product-card__swatch'];
    if (extraClass) classes.push(extraClass);
    if (isActive) classes.push('searchspring-product-card__swatch--active');
    if (isDisabled) classes.push('searchspring-product-card__swatch--disabled');

    // Escape all user-provided content to prevent XSS
    const escapedType = escapeHtml(type);
    const escapedValue = escapeHtml(value);
    const escapedTitle = escapeHtml(title);
    const escapedContent = escapeHtml(content);

    let combinedStyle = '';
    if (style && inlineStyle) {
      combinedStyle = `${style}; ${inlineStyle}`;
    } else if (style) {
      combinedStyle = style;
    } else if (inlineStyle) {
      combinedStyle = inlineStyle;
    }
    const escapedStyle = escapeHtml(combinedStyle);
    const styleAttribute = escapedStyle ? ` style="${escapedStyle}"` : '';

    const pressedAttribute = isActive ? 'aria-pressed="true"' : 'aria-pressed="false"';
    const disabledAttribute = isDisabled ? 'aria-disabled="true" disabled' : 'aria-disabled="false"';

    return `
            <button class="${classes.join(' ')}" type="button" data-type="${escapedType}" data-value="${escapedValue}" title="${escapedTitle}" aria-label="${escapedTitle}" ${pressedAttribute} ${disabledAttribute}${styleAttribute}>
                ${escapedContent}
            </button>
        `;
  }

  /**
   * @param {{ type: 'color' | 'size'; text: string; title?: string }} param0
   */
  /**
   * @param {{ type: 'color' | 'size'; text: string; title?: string }} param0
   * @returns {string}
   */
  renderMoreSwatches({ type, text, title = 'More options' }) {
    // Escape all user-provided content to prevent XSS
    const escapedType = escapeHtml(type);
    const escapedText = escapeHtml(text);
    const escapedTitle = escapeHtml(title);
    return `
            <button class="searchspring-product-card__more-swatches" type="button" data-type="${escapedType}" data-value="more" title="${escapedTitle}" aria-label="${escapedTitle}">
                ${escapedText}
            </button>
        `;
  }

  renderImagePlaceholder() {
    return `
      <div class="product-media product-media--placeholder" style="--ratio: 0.6666666666666666">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M375.5 345.2c0-.1 0-.1 0 0 0-.1 0-.1 0 0-1.1-2.9-2.3-5.5-3.4-7.8-1.4-4.7-2.4-13.8-.5-19.8 3.4-10.6 3.6-40.6 1.2-54.5-2.3-14-12.3-29.8-18.5-36.9-5.3-6.2-12.8-14.9-15.4-17.9 8.6-5.6 13.3-13.3 14-23 0-.3 0-.6.1-.8.4-4.1-.6-9.9-3.9-13.5-2.1-2.3-4.8-3.5-8-3.5h-54.9c-.8-7.1-3-13-5.2-17.5-6.8-13.9-12.5-16.5-21.2-16.5h-.7c-8.7 0-14.4 2.5-21.2 16.5-2.2 4.5-4.4 10.4-5.2 17.5h-48.5c-3.2 0-5.9 1.2-8 3.5-3.2 3.6-4.3 9.3-3.9 13.5 0 .2 0 .5.1.8.7 9.8 5.4 17.4 14 23-2.6 3.1-10.1 11.7-15.4 17.9-6.1 7.2-16.1 22.9-18.5 36.9-2.2 13.3-1.2 47.4 1 54.9 1.1 3.8 1.4 14.5-.2 19.4-1.2 2.4-2.3 5-3.4 7.9-4.4 11.6-6.2 26.3-5 32.6 1.8 9.9 16.5 14.4 29.4 14.4h176.8c12.9 0 27.6-4.5 29.4-14.4 1.2-6.5-.5-21.1-5-32.7zm-97.7-178c.3-3.2.8-10.6-.2-18 2.4 4.3 5 10.5 5.9 18h-5.7zm-36.3-17.9c-1 7.4-.5 14.8-.2 18h-5.7c.9-7.5 3.5-13.7 5.9-18zm4.5-6.9c0-.1.1-.2.1-.4 4.4-5.3 8.4-5.8 13.1-5.8h.7c4.7 0 8.7.6 13.1 5.8 0 .1 0 .2.1.4 3.2 8.9 2.2 21.2 1.8 25h-30.7c-.4-3.8-1.3-16.1 1.8-25zm-70.7 42.5c0-.3 0-.6-.1-.9-.3-3.4.5-8.4 3.1-11.3 1-1.1 2.1-1.7 3.4-2.1l-.6.6c-2.8 3.1-3.7 8.1-3.3 11.6 0 .2 0 .5.1.8.3 3.5.9 11.7 10.6 18.8.3.2.8.2 1-.2.2-.3.2-.8-.2-1-9.2-6.7-9.8-14.4-10-17.7 0-.3 0-.6-.1-.8-.3-3.2.5-7.7 3-10.5.8-.8 1.7-1.5 2.6-1.9h155.7c1 .4 1.9 1.1 2.6 1.9 2.5 2.8 3.3 7.3 3 10.5 0 .2 0 .5-.1.8-.3 3.6-1 13.1-13.8 20.1-.3.2-.5.6-.3 1 .1.2.4.4.6.4.1 0 .2 0 .3-.1 13.5-7.5 14.3-17.5 14.6-21.3 0-.3 0-.5.1-.8.4-3.5-.5-8.5-3.3-11.6l-.6-.6c1.3.4 2.5 1.1 3.4 2.1 2.6 2.9 3.5 7.9 3.1 11.3 0 .3 0 .6-.1.9-1.5 20.9-23.6 31.4-65.5 31.4h-43.8c-41.8 0-63.9-10.5-65.4-31.4zm91 89.1h-7c0-1.5 0-3-.1-4.2-.2-12.5-2.2-31.1-2.7-35.1h3.6c.8 0 1.4-.6 1.4-1.4v-14.1h2.4v14.1c0 .8.6 1.4 1.4 1.4h3.7c-.4 3.9-2.4 22.6-2.7 35.1v4.2zm65.3 11.9h-16.8c-.4 0-.7.3-.7.7 0 .4.3.7.7.7h16.8v2.8h-62.2c0-.9-.1-1.9-.1-2.8h33.9c.4 0 .7-.3.7-.7 0-.4-.3-.7-.7-.7h-33.9c-.1-3.2-.1-6.3-.1-9h62.5v9zm-12.5 24.4h-6.3l.2-1.6h5.9l.2 1.6zm-5.8-4.5l1.6-12.3h2l1.6 12.3h-5.2zm-57-19.9h-62.4v-9h62.5c0 2.7 0 5.8-.1 9zm-62.4 1.4h62.4c0 .9-.1 1.8-.1 2.8H194v-2.8zm65.2 0h7.3c0 .9.1 1.8.1 2.8H259c.1-.9.1-1.8.1-2.8zm7.2-1.4h-7.2c.1-3.2.1-6.3.1-9h7c0 2.7 0 5.8.1 9zm-7.7-66.7v6.8h-9v-6.8h9zm-8.9 8.3h9v.7h-9v-.7zm0 2.1h9v2.3h-9v-2.3zm26-1.4h-9v-.7h9v.7zm-9 3.7v-2.3h9v2.3h-9zm9-5.9h-9v-6.8h9v6.8zm-119.3 91.1c-2.1-7.1-3-40.9-.9-53.6 2.2-13.5 11.9-28.6 17.8-35.6 5.6-6.5 13.5-15.7 15.7-18.3 11.4 6.4 28.7 9.6 51.8 9.6h6v14.1c0 .8.6 1.4 1.4 1.4h5.4c.3 3.1 2.4 22.4 2.7 35.1 0 1.2.1 2.6.1 4.2h-63.9c-.8 0-1.4.6-1.4 1.4v16.1c0 .8.6 1.4 1.4 1.4H256c-.8 11.8-2.8 24.7-8 33.3-2.6 4.4-4.9 8.5-6.9 12.2-.4.7-.1 1.6.6 1.9.2.1.4.2.6.2.5 0 1-.3 1.3-.8 1.9-3.7 4.2-7.7 6.8-12.1 5.4-9.1 7.6-22.5 8.4-34.7h7.8c.7 11.2 2.6 23.5 7.1 32.4.2.5.8.8 1.3.8.2 0 .4 0 .6-.2.7-.4 1-1.2.6-1.9-4.3-8.5-6.1-20.3-6.8-31.1H312l-2.4 18.6c-.1.4.1.8.3 1.1.3.3.7.5 1.1.5h9.6c.4 0 .8-.2 1.1-.5.3-.3.4-.7.3-1.1l-2.4-18.6H333c.8 0 1.4-.6 1.4-1.4v-16.1c0-.8-.6-1.4-1.4-1.4h-63.9c0-1.5 0-2.9.1-4.2.2-12.7 2.3-32 2.7-35.1h5.2c.8 0 1.4-.6 1.4-1.4v-14.1h6.2c23.1 0 40.4-3.2 51.8-9.6 2.3 2.6 10.1 11.8 15.7 18.3 5.9 6.9 15.6 22.1 17.8 35.6 2.2 13.4 2 43.2-1.1 53.1-1.2 3.9-1.4 8.7-1 13-1.7-2.8-2.9-4.4-3-4.6-.2-.3-.6-.5-.9-.6h-.5c-.2 0-.4.1-.5.2-.6.5-.8 1.4-.3 2 0 0 .2.3.5.8 1.4 2.1 5.6 8.4 8.9 16.7h-42.9v-43.8c0-.8-.6-1.4-1.4-1.4s-1.4.6-1.4 1.4v44.9c0 .1-.1.2-.1.3 0 .1 0 .2.1.3v9c-1.1 2-3.9 3.7-10.5 3.7h-7.5c-.4 0-.7.3-.7.7 0 .4.3.7.7.7h7.5c5 0 8.5-.9 10.5-2.8-.1 3.1-1.5 6.5-10.5 6.5H210.4c-9 0-10.5-3.4-10.5-6.5 2 1.9 5.5 2.8 10.5 2.8h67.4c.4 0 .7-.3.7-.7 0-.4-.3-.7-.7-.7h-67.4c-6.7 0-9.4-1.7-10.5-3.7v-54.5c0-.8-.6-1.4-1.4-1.4s-1.4.6-1.4 1.4v43.8h-43.6c4.2-10.2 9.4-17.4 9.5-17.5.5-.6.3-1.5-.3-2s-1.5-.3-2 .3c-.1.2-1.4 2-3.2 5 .1-4.9-.4-10.2-1.1-12.8zm221.4 60.2c-1.5 8.3-14.9 12-26.6 12H174.4c-11.8 0-25.1-3.8-26.6-12-1-5.7.6-19.3 4.6-30.2H197v9.8c0 6.4 4.5 9.7 13.4 9.7h105.4c8.9 0 13.4-3.3 13.4-9.7v-9.8h44c4 10.9 5.6 24.5 4.6 30.2z"></path><path d="M286.1 359.3c0 .4.3.7.7.7h14.7c.4 0 .7-.3.7-.7 0-.4-.3-.7-.7-.7h-14.7c-.3 0-.7.3-.7.7zm5.3-145.6c13.5-.5 24.7-2.3 33.5-5.3.4-.1.6-.5.4-.9-.1-.4-.5-.6-.9-.4-8.6 3-19.7 4.7-33 5.2-.4 0-.7.3-.7.7 0 .4.3.7.7.7zm-11.3.1c.4 0 .7-.3.7-.7 0-.4-.3-.7-.7-.7H242c-19.9 0-35.3-2.5-45.9-7.4-.4-.2-.8 0-.9.3-.2.4 0 .8.3.9 10.8 5 26.4 7.5 46.5 7.5h38.1zm-7.2 116.9c.4.1.9.1 1.4.1 1.7 0 3.4-.7 4.7-1.9 1.4-1.4 1.9-3.2 1.5-5-.2-.8-.9-1.2-1.7-1.1-.8.2-1.2.9-1.1 1.7.3 1.2-.4 2-.7 2.4-.9.9-2.2 1.3-3.4 1-.8-.2-1.5.3-1.7 1.1s.2 1.5 1 1.7z"></path><path d="M275.5 331.6c-.8 0-1.4.6-1.5 1.4 0 .8.6 1.4 1.4 1.5h.3c3.6 0 7-2.8 7.7-6.3.2-.8-.4-1.5-1.1-1.7-.8-.2-1.5.4-1.7 1.1-.4 2.3-2.8 4.2-5.1 4zm5.4 1.6c-.6.5-.6 1.4-.1 2 1.1 1.3 2.5 2.2 4.2 2.8.2.1.3.1.5.1.6 0 1.1-.3 1.3-.9.3-.7-.1-1.6-.8-1.8-1.2-.5-2.2-1.2-3-2.1-.6-.6-1.5-.6-2.1-.1zm-38.2 12.7c.5 0 .9 0 1.4-.1.8-.2 1.3-.9 1.1-1.7-.2-.8-.9-1.3-1.7-1.1-1.2.3-2.5-.1-3.4-1-.4-.4-1-1.2-.8-2.4.2-.8-.3-1.5-1.1-1.7-.8-.2-1.5.3-1.7 1.1-.4 1.8.1 3.7 1.5 5 1.2 1.2 2.9 1.9 4.7 1.9z"></path><path d="M241.2 349.6h.3c.8 0 1.4-.7 1.4-1.5s-.7-1.4-1.5-1.4c-2.3.1-4.6-1.7-5.1-4-.2-.8-.9-1.3-1.7-1.1-.8.2-1.3.9-1.1 1.7.7 3.5 4.1 6.3 7.7 6.3zm-9.7 3.6c.2 0 .3 0 .5-.1 1.6-.6 3-1.6 4.2-2.8.5-.6.5-1.5-.1-2s-1.5-.5-2 .1c-.8.9-1.8 1.6-3 2.1-.7.3-1.1 1.1-.8 1.8 0 .6.6.9 1.2.9z"></path></svg>
      </div>
      `
  }

  /**
   * @param {SearchspringProduct} product
   * @returns {string}
   */
  renderFabric(product) {
    if (
      !product ||
      !product?.mfield_searchspring_fabric
    )
      return '';

    return `
      <div class="searchspring-product-card__fabric">${product.mfield_searchspring_fabric}</div>
    `;
  }
}

if (!customElements.get('searchspring-product-card')) {
  customElements.define('searchspring-product-card', SearchspringProductCard);
}
